import Button from '@components/v2/ui/Button/Button';
import Icon from '@components/v2/ui/Icon/Icon';
import { Box, Stack } from '@mui/material';
import { Plus } from 'lucide-react';
import useMeasuredWidth from '../../../../../../../../hooks/useMeasuredWidth';
import LangEnum from '../../../../../../../../lang/LangEnum';
import { UsedInOfflineMode } from '../../../../../../../offline/types/UsedInOfflineMode';
import { useTranslation } from '../../../../../../../translation/TranslationContext';
import OrderInvoicesButton from '../../../../../../conditions/components/OrderInvoicesButton';
import { OrderCondition } from '../../../../../../conditions/interfaces/orderCondition.interface';
import { Order } from '../../../../../../order.types';
import { CONDITION_GROUP_GAP } from '../../orderObjectConditionsTab.consts';
import OrderConditionReadOnlyField from '../OrderConditionReadOnlyField';
import OrderConditionSectionLabel from '../OrderConditionSectionLabel';
import { getConditionFieldsGridSx } from '../orderConditionDetails.utils';
import OrderConditionReadOnlyDeliverySection from './components/OrderConditionReadOnlyDeliverySection';
import useOrderConditionDetailsReadOnly from './hooks/useOrderConditionDetailsReadOnly';

interface OrderConditionDetailsReadOnlyProps extends UsedInOfflineMode {
  order: Order;
  condition: OrderCondition;
  onOpenMoreConditions: () => void;
  onOpenOrderInvoices: () => void;
}

const OrderConditionDetailsReadOnly = ({
  order,
  condition,
  isOfflineMode,
  onOpenMoreConditions,
  onOpenOrderInvoices,
}: OrderConditionDetailsReadOnlyProps) => {
  const { translate } = useTranslation();
  const { containerRef, width: containerWidth } = useMeasuredWidth();
  const {
    isDetailsSectionVisible,
    isBillingForecastDateVisible,
    isVoucherVisible,
    isDeliverySectionVisible,
    isShippingCostVisible,
    isObservationVisible,
    isInternalObservationVisible,
    isMoreConditionsVisible,
    isOrderInvoicesVisible,
    paymentMethodValue,
    billingForecastDateValue,
    deliveryAddressValue,
    discountValue,
    voucherValue,
    shippingCostValue,
  } = useOrderConditionDetailsReadOnly({ order, condition, isOfflineMode });

  return (
    <Stack ref={containerRef} sx={{ gap: CONDITION_GROUP_GAP }}>
      {isDetailsSectionVisible && (
        <Stack sx={{ gap: 2 }}>
          <OrderConditionSectionLabel label={LangEnum.GLOBAL_DETAILS} />

          <Box sx={getConditionFieldsGridSx(isBillingForecastDateVisible, containerWidth)}>
            <OrderConditionReadOnlyField
              label={LangEnum.GLOBAL_PAYMENT_METHOD}
              value={paymentMethodValue}
            />

            {isBillingForecastDateVisible && (
              <OrderConditionReadOnlyField
                label={LangEnum.ORDER_BILLING_FORECAST_DATE}
                value={billingForecastDateValue}
              />
            )}
          </Box>

          <Box sx={getConditionFieldsGridSx(isVoucherVisible, containerWidth)}>
            <OrderConditionReadOnlyField label={LangEnum.GLOBAL_DISCOUNT} value={discountValue} />

            {isVoucherVisible && (
              <OrderConditionReadOnlyField label={LangEnum.GLOBAL_VOUCHER} value={voucherValue} />
            )}
          </Box>
        </Stack>
      )}

      {isDeliverySectionVisible && (
        <OrderConditionReadOnlyDeliverySection
          deliveryAddressValue={deliveryAddressValue}
          shippingCostValue={shippingCostValue}
          visibility={{ isShippingCostVisible }}
        />
      )}

      <Stack sx={{ gap: 2 }}>
        {isInternalObservationVisible && (
          <OrderConditionReadOnlyField
            label={LangEnum.ORDER_CONDITIONS_INTERNAL_OBSERVATION_LABEL}
            value={condition.internalObservation}
          />
        )}

        {isObservationVisible && (
          <OrderConditionReadOnlyField
            label={LangEnum.ORDER_CONDITIONS_OBSERVATION_LABEL}
            value={condition.observation}
          />
        )}

        <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
          {isMoreConditionsVisible && (
            <Button
              variant="contained"
              color="secondary"
              shape="square"
              startIcon={<Icon icon={Plus} size="md" />}
              onClick={onOpenMoreConditions}
            >
              {translate(LangEnum.ORDER_CONDITION_SEE_MORE_CONDITION_LABEL)}
            </Button>
          )}

          {isOrderInvoicesVisible && (
            <OrderInvoicesButton hasInvoices={!!order.hasInvoices} onClick={onOpenOrderInvoices} />
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default OrderConditionDetailsReadOnly;
